import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { Lock, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const ResetPasswordPage = () => {
  const { t, lang, setLang, LANGS } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError(t('rpLinkInvalid'));
        setValidating(false);
        return;
      }

      try {
        await api.get(`/auth/password-reset/validate/${token}`);
        setTokenValid(true);
      } catch (error) {
        setTokenError(t('rpInvalidLink'));
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen haben');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/password-reset/confirm', {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success('Passwort erfolgreich zurückgesetzt!');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Fehler beim Zurücksetzen';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">{t('rpValidating')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Language Selector - Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <select
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          className="bg-white/90 backdrop-blur border border-gray-300 rounded-md px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="language-selector"
        >
          {LANGS.map((l) => (
            <option key={l.code} value={l.code}>
              {l.flag} {l.label}
            </option>
          ))}
        </select>
      </div>

      {/* Mobile Hero Image */}
      <div
        className="lg:hidden h-48 relative"
        style={{
          backgroundImage: 'url(https://customer-assets-agu9un31.emergentagent.net/job_79549384-9d12-485d-8143-ee256e051cae/artifacts/1s5py9ht_ChatGPT%20Image%2026.%20Juli%202026%2C%2017_54_31.png)',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#1a1a1a',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />
      </div>

      {/* Desktop Hero Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-center justify-center"
        style={{
          backgroundColor: '#1a1a1a',
        }}
      >
        <div
          className="w-full h-full"
          style={{
            backgroundImage: 'url(https://customer-assets-agu9un31.emergentagent.net/job_79549384-9d12-485d-8143-ee256e051cae/artifacts/1s5py9ht_ChatGPT%20Image%2026.%20Juli%202026%2C%2017_54_31.png)',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_barber-supply/artifacts/e1ft8m5t_ED_STEEL_Logo_White_BG_Print.png" 
              alt="ED STEEL" 
              className="h-24 w-auto mx-auto mb-6"
            />
          </div>

          {/* Invalid Token State */}
          {!tokenValid && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-heading font-bold mb-2">{t('rpLinkInvalid')}</h2>
              <p className="text-muted-foreground mb-6">{tokenError}</p>
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full h-12"
                data-testid="request-new-link"
              >
                {t('rpRequestNew')}
              </Button>
            </div>
          )}

          {/* Success State */}
          {tokenValid && success && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-heading font-bold mb-2">{t('rpChanged')}</h2>
              <p className="text-muted-foreground mb-6">
                {t('rpChangedText')}
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-12"
                data-testid="go-to-login"
              >
                {t('rpToLogin')}
              </Button>
            </div>
          )}

          {/* Reset Form */}
          {tokenValid && !success && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl font-heading font-bold mb-2">{t('newPassword')}</h2>
                <p className="text-muted-foreground">
                  {t('rpEnterNew')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="newPassword">{t('newPassword')}</Label>
                  <div className="relative mt-2">
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      className="h-12 pr-10"
                      placeholder={t('rpMin8')}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      data-testid="toggle-password-visibility"
                      aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="h-12 mt-2"
                    placeholder={t('rpRepeat')}
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-lg font-heading"
                  disabled={loading}
                  data-testid="submit-button"
                >
                  {loading ? t('rpSaving') : t('rpSave')}
                </Button>
              </form>
            </>
          )}

          <div className="mt-8 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center text-primary font-medium hover:underline"
              data-testid="back-to-login"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
